import { create } from "zustand";
import { toast } from "sonner";
import http from "@/lib/http";
import { devtools } from "zustand/middleware";

const usePropertyStore = create(
  devtools((set) => ({
    properties: [],
    pages:0,
    result:[],
    investedProperties: [],
    listedProperties: [],
    availableProperties: [],
    loading: false,
    error: null,
    currentProperty: {
      data: {},
      loading: false,
      error: null,
    },

    getAllProperties: async ({
      page,
      status,
      limit,
      country,
      city,
      zip_code,
      type,
      state,
    }) => {
      set({ loading: true, error: null });

      const params = {
        page,
        limit,
        status,
        ...(type ? { type } : {}),
        ...(country ? { country } : {}),
        ...(state ? { state } : {}),
        ...(city ? { city } : {}),
        ...(zip_code ? { zip_code } : {}),
      };

      try {
        const { data } = await http.get(`/user/property`, { params });
        set({ properties: data?.result?.data, loading: false, pages:data?.result?.pages });
        return data;
      } catch (error) {
        const errorMessage =
          error?.response?.data?.error || "Failed to fetch properties";
        set({ error: errorMessage, loading: false });
        toast.error(errorMessage);
      }
    },

    getProperty: async (id) => {
      
      set({ currentProperty: { data: {}, loading: true, error: null } });

      // Get the existing IDs from sessionStorage or initialize as an empty array
      const storedIds = JSON.parse(sessionStorage.getItem("marketTokenIds")) || [];

      // Check if the ID is already stored
      const isIdStored = storedIds.includes(id);

      // If the ID is already stored, return the boolean value
      

      try {
        const { data } = await http.get(`/user/property/${id}${!isIdStored ? "?increment=true" : ""}`);
        set({
          currentProperty: {
            data: data?.result,
            loading: false,
            error: null,
          },
        });
        
        // Save the new ID to sessionStorage only once
        storedIds.push(id);
        sessionStorage.setItem("marketTokenIds", JSON.stringify(storedIds));
        return data;
      } catch (error) {
        const errorMessage =
          error?.response?.data?.error || "Failed to fetch property";
        set({
          currentProperty: { data: {}, loading: false, error: errorMessage },
        });
        toast.error(errorMessage);
      }
    },

    getAllPropertiesList: async ({ page, status, limit, country, city }) => {
      set({ loading: true, error: null });

      const params = {
        page,
        limit,
        status,
        ...(country ? { country } : {}),
        ...(city ? { city } : {}),
      };

      try {
        const { data } = await http.get(`/user/property/list`, { params });
        set({ availableProperties: data?.result?.data, loading: false });
        return data;
      } catch (error) {
        const errorMessage =
          error?.response?.data?.error || "Failed to fetch properties";
        set({ error: errorMessage, loading: false });
        toast.error(errorMessage);
      }
    },

    getInvestedProperties: async () => {
      set({ loading: true, error: null });

      try {
        const { data } = await http.get("/user/property/invested");
        set({ investedProperties: data?.properties?.data, loading: false });
        return data;
      } catch (error) {
        const errorMessage =
          error?.response?.data?.error || "Failed to fetch properties";
        set({ error: errorMessage, loading: false });
        toast.error(errorMessage);
      }
    },
    getListedProperties: async () => {
      set({ loading: true, error: null });

      try {
        const { data } = await http.get("/user/property/listed");
        set({ listedProperties: data?.properties?.data, loading: false });
        return data;
      } catch (error) {
        const errorMessage =
          error?.response?.data?.error || "Failed to fetch properties";
        set({ error: errorMessage, loading: false });
        toast.error(errorMessage);
      }
    },

    createProperty: async (formData) => {
      set({ loading: true, error: null });

      try {
        const { data } = await http.post("/user/property", formData, {
          isMultipart: true,
        });

        set({ loading: false, error: null });

        if (data.success) {
          toast.success(data?.message || "Property created successfully");
        }

        return data;
      } catch (error) {
        const errorMessage =
          error?.response?.data?.error || "Failed to create token";
        set({ error: errorMessage, loading: false });
        toast.error(errorMessage);
      }
    },

    // Buy property
    buyProperty: async (propertyId, usdcAmount, propertyAmount,lockTime) => {
      set({ loading: true, error: null });

      try {
        const { data } = await http.post(`/user/property/${propertyId}/buy`, {
          usdcAmount,
          propertyAmount,
          lockTime
        });
        set({ loading: false });

        if (data?.success) {
          toast.success("Tokens bought successfully");
        }
        return data;
      } catch (error) {
    
        const errorMessage =
          error?.response?.data?.error || "Failed to buy token";
        set({ error: errorMessage, loading: false });
        toast.error(errorMessage);
      }
    },

    // Sell property
    sellProperty: async (propertyId, usdcAmount, propertyAmount) => {
      set({ loading: true, error: null });

      try {
        const { data } = await http.post(`/user/property/${propertyId}/sell`, {
          usdcAmount,
          propertyAmount,
        });

        set({ loading: false });

        if (data?.success) {
          toast.success(data.message);
        }

        return data;
      } catch (error) {
        const errorMessage =
          error?.response?.data?.error || "Failed to sell token";
        set({ error: errorMessage, loading: false });
        toast.error(errorMessage);
      }
    },
    getTransactions: async () => {
      set({ loading: true, error: null });
      try {
        const { data } = await http.get("wallet/billing-history");
        set({ transactions: data.history, loading: false });
        return data;
      } catch (error) {
        const errorMessage =
          error?.response?.data?.message || "Failed to fetch transactions";
        set({ error: errorMessage, loading: false });
        toast.error(errorMessage);
      }
    },
    getPriceChange: async (propertyId) => {
      set({ loading: true, error: null });
      try {
        const { data } = await http.get(`/public/property/price-change/${propertyId}`);
        set({ result: data.result, loading: false });
        return data;
      } catch (error) {
        const errorMessage =
          error?.response?.data?.message || "Failed to fetch transactions";
        set({ error: errorMessage, loading: false });
        toast.error(errorMessage);
      }
    },
  })),
);

export default usePropertyStore;
